/**
 * Maps a booking display status to a design-system `<Badge>` variant + label. Shared by the
 * bookings tab cards and the read-only booking-details dialog so status visuals stay consistent.
 *
 * The domain rules — priority resolution (`resolveBookingDisplayStatus`) and the user-facing
 * label (`bookingDisplayStatusLabel`, e.g. approved→"Pending", booked→"Booked") — live in
 * `@elara/domain/booking-status` so web and native agree. This file owns only the web-specific
 * variant mapping (the `<Badge>` colour names).
 */

import type { BadgeVariant } from '@elara/client-core/ui-contracts/badge-variant';
import type { BookingDisplayStatus } from '@elara/domain/booking-status';

import type { IconName } from '#ui/MaterialIcon.js';

import { bookingDisplayStatusLabel } from '@elara/domain/booking-status';

export type { BookingDisplayStatus } from '@elara/domain/booking-status';
export { resolveBookingDisplayStatus } from '@elara/domain/booking-status';

export function bookingStatusBadgeProps(
  status: BookingDisplayStatus,
): { variant: BadgeVariant; label: string } {
  return { variant: bookingStatusVariant(status), label: bookingDisplayStatusLabel(status) };
}

/** Web-only: the `<Badge>` colour for each display status. */
function bookingStatusVariant(status: BookingDisplayStatus): BadgeVariant {
  switch (status) {
    case 'planned':
      return 'info';
    case 'approved':
      return 'info';
    case 'booked':
      return 'success';
    case 'cancellation_pending':
      return 'secondary';
    case 'cancelled':
      return 'destructive';
    case 'unavailable':
      return 'destructive';
  }
}

/** Web-only: the text colour for each display status. */
export function bookingStatusLabelColor(status: BookingDisplayStatus): string {
  switch (status) {
    case 'planned':
      return 'text-yellow-700 dark:text-yellow-400';
    case 'approved':
      return 'text-success';
    case 'booked':
      return 'text-success';
    case 'cancellation_pending':
      return 'text-muted-foreground';
    case 'cancelled':
      return 'text-destructive';
    case 'unavailable':
      return 'text-destructive';
  }
}

/**
 * Web-only: the itinerary builder card's left status bar colour (mirrors the mobile client's
 * `resolveStatusTone`). `status` is undefined for an event that hasn't been assigned one yet — that
 * reads as "not booked", a warning, UNLESS the event type isn't bookable (`isBookable` false, e.g. a
 * place/activity/custom item), where "not booked" is just its normal resting state and gets no bar.
 */
export function bookingStatusBorderColor(status: BookingDisplayStatus | undefined, isBookable: boolean): string {
  switch (status) {
    case 'approved':
    case 'booked':
      return 'border-l-green-600';
    case 'cancellation_pending':
      return 'border-l-muted-foreground';
    case 'cancelled':
    case 'unavailable':
      return 'border-l-red-600';
    default:
      return isBookable ? 'border-l-yellow-500' : 'border-l-transparent';
  }
}

/** A status-appropriate glyph (mirrors the mobile client's `bookingStatusIcon`). */
export function bookingStatusIcon(status: BookingDisplayStatus): IconName {
  switch (status) {
    case 'booked':
      return 'check_circle';
    case 'approved':
      return 'pending';
    case 'cancellation_pending':
      return 'hourglass_top';
    case 'cancelled':
      return 'cancel';
    case 'unavailable':
      return 'block';
    case 'planned':
      return 'event';
  }
}
